// src/utils/apiClient.js

import { api } from "../api/config";

// In-memory cache for instant GET requests & zero-delay page navigation
const cacheMap = new Map();
const CACHE_TTL_MS = 10000; // 10 seconds SWR window

function normalize(url) {
  if (!url.startsWith("/")) {
    url = "/" + url;
  }

  // DO NOT prepend /api here.
  // baseURL already ends with /api.
  return url;
}

const apiClient = {
  get: async (url, config = {}) => {
    const normUrl = normalize(url);
    const cacheKey = normUrl + JSON.stringify(config.params || {});

    if (!config.bypassCache && cacheMap.has(cacheKey)) {
      const entry = cacheMap.get(cacheKey);
      const isFresh = Date.now() - entry.timestamp < CACHE_TTL_MS;

      if (!isFresh) {
        api
          .get(normUrl, config)
          .then((freshRes) => {
            cacheMap.set(cacheKey, {
              data: freshRes,
              timestamp: Date.now(),
            });
          })
          .catch(() => {});
      }

      return entry.data;
    }

    const res = await api.get(normUrl, config);

    if (!config.bypassCache) {
      cacheMap.set(cacheKey, {
        data: res,
        timestamp: Date.now(),
      });
    }

    return res;
  },

  post: async (url, data, config = {}) => {
    cacheMap.clear();
    return api.post(normalize(url), data, config);
  },

  put: async (url, data, config = {}) => {
    cacheMap.clear();
    return api.put(normalize(url), data, config);
  },

  delete: async (url, config = {}) => {
    cacheMap.clear();
    return api.delete(normalize(url), config);
  },

  patch: async (url, data, config = {}) => {
    cacheMap.clear();
    return api.patch(normalize(url), data, config);
  },

  clearCache: () => cacheMap.clear(),
};

api.interceptors.response.use(
  (res) => res,
  (error) => {
    console.error("API ERROR:", error?.response || error);
    return Promise.reject(error);
  }
);

export default apiClient;
